import { API_CONFIG } from "config/api.config";
import { api } from "utils/api";
import { isValidCustomerId, parseCustomerId } from "utils/customerUtils";

/**
 * Billing API — same console logging shape as utils/api.js fetchInstance.
 */

const redactBillingPayload = (requestData) => ({
  ...requestData,
  "api-key": "***",
});

const logBillingRequest = (fullUrl, headers, requestData) => {
  const method = "POST";
  console.group("🌐 API Request");
  console.log("URL:", fullUrl);
  console.log("Method:", method);
  console.log("Headers:", {
    ...headers,
    "X-API-Key": headers["X-API-Key"] ? "***" : undefined,
  });
  console.log("Request Body:", redactBillingPayload(requestData));
  console.groupEnd();
};

const logBillingResponse = (response, responseData) => {
  console.group("📥 API Response");
  console.log("Status:", response.status);
  console.log("Status Text:", response.statusText);
  if (response.status === 204) {
    console.log("Response: No Content (204)");
  } else {
    console.log("Response Data:", responseData);
  }
  console.groupEnd();
};

const logBillingApiError = (fullUrl, method, requestData, error) => {
  console.group("❌ API Error");
  console.error("Error:", error);
  console.error("Request URL:", fullUrl);
  console.error("Request Method:", method);
  console.error("Request Body:", redactBillingPayload(requestData));
  console.groupEnd();
};

const BILLING_HEADERS = { "Content-Type": "text/plain" };

const buildBillingRequestPayload = (requestData) => {
  const { useProxy } = API_CONFIG.billingApi;
  if (!useProxy) {
    return requestData;
  }
  return {
    resource: requestData.resource,
    request: requestData.request,
    parameters: requestData.parameters,
  };
};

const buildBillingFetchHeaders = () => {
  const { apiKey, useProxy } = API_CONFIG.billingApi;
  if (useProxy) {
    return {
      "Content-Type": "application/json",
      "X-API-Key": apiKey,
    };
  }
  return BILLING_HEADERS;
};

const balanceUnreachableMessage = (error) => {
  if (!error) return "Cannot reach billing service.";
  const msg = String(error.message || error);
  if (msg.includes("Failed to fetch") || error?.name === "TypeError") {
    return "Cannot reach billing service. Check that it is running.";
  }
  return "Cannot reach billing service.";
};

const customerExistsInBilling = async (customerId) => {
  const requestData = {
    "api-key": API_CONFIG.billingApi.apiKey,
    resource: "customers",
    request: "get",
    parameters: {
      customer_id: customerId,
    },
  };

  const { ok, data } = await billingFetch(requestData);
  if (!ok || Number(data?.status) !== 200) return false;

  const reply = data?.reply;
  if (Array.isArray(reply)) {
    return reply.some((row) => Number(row?.customer_id) === Number(customerId));
  }
  if (reply && typeof reply === "object") {
    return Number(reply?.customer_id) === Number(customerId);
  }
  return false;
};

/**
 * @returns {{ ok: true, response: Response, data: unknown } | { ok: false, response: Response | null, data: unknown, networkError?: Error }}
 */
const billingFetch = async (requestData) => {
  const fullUrl = API_CONFIG.billingApi.baseUrl;
  const method = "POST";
  const payload = buildBillingRequestPayload(requestData);
  const headers = buildBillingFetchHeaders();
  const body = JSON.stringify(payload);

  logBillingRequest(fullUrl, headers, requestData);

  let response;
  try {
    response = await fetch(fullUrl, {
      method,
      headers,
      body,
    });
  } catch (error) {
    logBillingApiError(fullUrl, method, requestData, error);
    return { ok: false, response: null, data: null, networkError: error };
  }

  const rawText = await response.text();
  let data = null;
  if (rawText) {
    try {
      data = JSON.parse(rawText);
    } catch {
      data = { message: rawText };
    }
  }

  logBillingResponse(response, data);

  if (!response.ok) {
    logBillingApiError(
      fullUrl,
      method,
      requestData,
      new Error(`HTTP error! status: ${response.status}`)
    );
    return { ok: false, response, data };
  }

  return { ok: true, response, data };
};

/**
 * Fetches customer balance from the billing API
 * @param {number} customerId - The customer ID to fetch balance for
 * @returns {Promise<
 *   | { ok: true; balance: number; lastUpdated: string }
 *   | { ok: false; errorMessage: string }
 * >}
 */
export const getCustomerBalance = async (customerId) => {
  if (!String(API_CONFIG.billingApi.baseUrl || "").trim()) {
    return { ok: false, errorMessage: "Billing API URL is not configured (FRONTEND_BILLING_API_BASE_URL)." };
  }
  if (!String(API_CONFIG.billingApi.apiKey || "").trim()) {
    const missingKeyMessage = API_CONFIG.billingApi.useProxy
      ? "Admin API key is not configured (FRONTEND_API_KEY)."
      : "Billing API key is not configured (FRONTEND_BILLING_API_KEY).";
    return { ok: false, errorMessage: missingKeyMessage };
  }

  const requestData = {
    "api-key": API_CONFIG.billingApi.apiKey,
    resource: "balance",
    request: "get",
    parameters: {
      customer_id: customerId,
    },
  };

  try {
    const { ok, data, response, networkError } = await billingFetch(requestData);

    if (!ok) {
      if (networkError) {
        return { ok: false, errorMessage: balanceUnreachableMessage(networkError) };
      }
      const status = response?.status ?? "error";
      const detail =
        typeof data?.reply === "string"
          ? data.reply
          : typeof data?.message === "string"
            ? data.message
            : null;
      return {
        ok: false,
        errorMessage: detail
          ? `Billing service error (HTTP ${status}): ${detail}`
          : `Billing service error (HTTP ${status}).`,
      };
    }

    if (data.status === 200 && data.reply && typeof data.reply.balance !== "undefined") {
      const balance = Number(data.reply.balance);

      if (isNaN(balance)) {
        console.warn("Invalid balance received from API:", data.reply.balance);
        return {
          ok: false,
          errorMessage: "Billing service returned an invalid balance value.",
        };
      }

      return {
        ok: true,
        balance,
        lastUpdated: new Date().toISOString(),
      };
    }

    // Some billing deployments return this when a customer exists but has no
    // register lines yet. Verify existence via customers/get before returning 0.
    if (data?.status === 200 && data?.reply === "Request returned no data.") {
      const exists = await customerExistsInBilling(customerId);
      if (!exists) {
        return {
          ok: false,
          errorMessage: "Billing service returned no balance data and customer lookup failed.",
        };
      }
      return {
        ok: true,
        balance: 0,
        lastUpdated: new Date().toISOString(),
      };
    }

    const apiStatus = data?.status;
    const replyMsg = typeof data?.reply === "string" ? data.reply : null;
    return {
      ok: false,
      errorMessage: replyMsg
        ? `Billing service returned an error (status ${apiStatus ?? "unknown"}): ${replyMsg}`
        : "Billing service did not return a balance (unexpected response).",
    };
  } catch (error) {
    logBillingApiError(
      API_CONFIG.billingApi.baseUrl,
      "POST",
      requestData,
      error
    );
    return { ok: false, errorMessage: balanceUnreachableMessage(error) };
  }
};

/**
 * Fetches customer statement from the billing API
 * @param {number} customerId - The customer ID to fetch statement for
 * @param {string} startDate - Start date in YYYY-MM-DD format
 * @param {string} endDate - End date in YYYY-MM-DD format
 * @returns {Promise<Object>} - Returns the statement data or empty object if error
 */
export const getCustomerStatement = async (customerId, startDate, endDate) => {
  const requestData = {
    "api-key": API_CONFIG.billingApi.apiKey,
    resource: "customer_statement",
    request: "get",
    parameters: {
      customer_id: customerId,
      start_date: startDate,
      end_date: endDate,
    },
  };

  try {
    const { ok, data } = await billingFetch(requestData);

    if (!ok) {
      return {};
    }

    if (data.status === 200 && data.reply) {
      return data.reply;
    }

    return {};
  } catch (error) {
    logBillingApiError(
      API_CONFIG.billingApi.baseUrl,
      "POST",
      requestData,
      error
    );
    return {};
  }
};

/**
 * Submits a customer payment to the billing API
 * @param {Object} payload - Payment payload
 * @param {number} payload.customerId - Billing customer ID
 * @param {string} payload.datetime - Payment datetime (YYYY-MM-DD HH:mm:ss)
 * @param {string} payload.payment - Payment method label (Cash/Bank/Card)
 * @param {number} payload.amount - Payment amount
 * @returns {Promise<Object>} - Billing API response shape { status, reply }
 */
export const submitPayment = async ({ customerId, datetime, payment, amount }) => {
  const requestData = {
    "api-key": API_CONFIG.billingApi.apiKey,
    resource: "payment",
    request: "put",
    parameters: {
      customer_id: customerId,
      datetime,
      payment,
      amount,
    },
  };

  try {
    const { ok, data, response } = await billingFetch(requestData);

    if (!ok) {
      const reply =
        typeof data?.reply !== "undefined"
          ? data.reply
          : response
            ? `HTTP error ${response.status}`
            : "Billing API request failed.";
      return {
        status: response?.status ?? 500,
        reply,
      };
    }

    if (typeof data?.status !== "undefined") {
      return data;
    }

    return {
      status: 500,
      reply: "Unexpected billing API response format.",
    };
  } catch (error) {
    logBillingApiError(
      API_CONFIG.billingApi.baseUrl,
      "POST",
      requestData,
      error
    );
    return {
      status: 500,
      reply: error?.message || "Failed to submit payment.",
    };
  }
};

const formatIsoDate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

/**
 * Default statement PDF range: first day of the calendar month three months ago through today.
 */
export const getDefaultStatementPdfDateRange = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - 3, 1);
  return {
    startDate: formatIsoDate(start),
    endDate: formatIsoDate(now),
  };
};

/** First calendar day of the month containing ``dateInput`` (YYYY-MM-DD). */
export const normalizeStatementStartDate = (dateInput) => {
  const text = String(dateInput || "").trim();
  if (!text) {
    return "";
  }
  const match = /^(\d{4})-(\d{2})/.exec(text);
  if (!match) {
    return text;
  }
  return `${match[1]}-${match[2]}-01`;
};

/** Build API statement range: start = 1st of selected month, end = chosen day. */
export const buildStatementDateRange = ({ startInput, endInput }) => ({
  startDate: normalizeStatementStartDate(startInput),
  endDate: String(endInput || "").trim(),
});

const fetchBackendStatementPdfBlob = async (
  billingCustomerId,
  { startDate, endDate, notes = "" },
  mode
) => {
  const billingId = Number(billingCustomerId || 0);
  if (billingId < 1) {
    throw new Error("Invalid billing customer id.");
  }
  const start = String(startDate || "").trim();
  const end = String(endDate || "").trim();
  if (!start || !end) {
    throw new Error("Statement start_date and end_date are required.");
  }
  const params = new URLSearchParams({ start_date: start, end_date: end });
  const noteText = String(notes || "").trim();
  if (noteText) {
    params.set("notes", noteText);
  }
  const endpoint = String(mode || "").trim() === "download" ? "download" : "view";
  const response = await fetch(
    `${API_CONFIG.baseUrl}/db/billing/statements/${billingId}/${endpoint}?${params.toString()}`,
    {
      method: "GET",
      headers: {
        "X-API-Key": API_CONFIG.apiKey,
        Accept: "application/pdf",
      },
    }
  );
  if (!response.ok) {
    let message = `Statement ${endpoint} failed (HTTP ${response.status}).`;
    try {
      const err = await response.json();
      if (err?.error) {
        message = String(err.error);
      }
    } catch {
      // keep generic message
    }
    throw new Error(message);
  }
  return response.blob();
};

export const getStatementPdfPreviewBlob = async (billingCustomerId, dateRange) =>
  fetchBackendStatementPdfBlob(billingCustomerId, dateRange, "view");

export const downloadStatementPdf = async (
  billingCustomerId,
  dateRange,
  filenameOverride = ""
) => {
  const blob = await fetchBackendStatementPdfBlob(billingCustomerId, dateRange, "download");
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  const cleanFilename = String(filenameOverride || "").trim();
  const { startDate, endDate } = dateRange || getDefaultStatementPdfDateRange();
  link.download =
    cleanFilename || `statement_${startDate}_to_${endDate}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(objectUrl);
};

export const sendStatementPdfByEmailWithContext = async ({
  billingCustomerId,
  startDate,
  endDate,
  toEmail,
  notes,
  customerId,
  customerCode,
}) => {
  const payload = {
    start_date: String(startDate || "").trim(),
    end_date: String(endDate || "").trim(),
  };
  const email = String(toEmail || "").trim();
  if (email) {
    payload.to_email = email;
  }
  const noteText = String(notes || "").trim();
  if (noteText) {
    payload.notes = noteText;
  }
  if (isValidCustomerId(customerId)) {
    payload.customer_id = parseCustomerId(customerId);
  }
  if (String(customerCode || "").trim()) {
    payload.customer_code = String(customerCode || "").trim();
  }
  return api.post(
    `/db/billing/statements/${Number(billingCustomerId || 0)}/email`,
    payload
  );
};

const fetchBackendInvoicePdfBlob = async (invoiceId, mode) => {
  const id = Number(invoiceId || 0);
  if (id < 1) {
    throw new Error("Invalid invoice id.");
  }
  const endpoint = String(mode || "").trim() === "download" ? "download" : "view";
  const response = await fetch(`${API_CONFIG.baseUrl}/db/billing/invoices/${id}/${endpoint}`, {
    method: "GET",
    headers: {
      "X-API-Key": API_CONFIG.apiKey,
      Accept: "application/pdf",
    },
  });
  if (!response.ok) {
    let message = `Invoice ${endpoint} failed (HTTP ${response.status}).`;
    try {
      const err = await response.json();
      if (err?.error) {
        message = String(err.error);
      }
    } catch {
      // keep generic message if error body is not JSON
    }
    throw new Error(message);
  }
  return response.blob();
};

export const getInvoicePdfPreviewBlob = async (invoiceId) =>
  fetchBackendInvoicePdfBlob(invoiceId, "view");

export const downloadInvoicePdf = async (invoiceId, filenameOverride = "") => {
  const blob = await fetchBackendInvoicePdfBlob(invoiceId, "download");
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  const cleanFilename = String(filenameOverride || "").trim();
  link.download = cleanFilename || `invoice_${Number(invoiceId || 0)}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(objectUrl);
};

export const sendInvoicePdfByEmail = async ({ invoiceId, toEmail }) => {
  const payload = {};
  const email = String(toEmail || "").trim();
  if (email) {
    payload.to_email = email;
  }
  return api.post(`/db/billing/invoices/${Number(invoiceId || 0)}/email`, payload);
};

export const sendInvoicePdfByEmailWithContext = async ({
  invoiceId,
  toEmail,
  customerId,
  documentNumber,
}) => {
  const payload = {};
  const email = String(toEmail || "").trim();
  if (email) {
    payload.to_email = email;
  }
  if (isValidCustomerId(customerId)) {
    payload.customer_id = parseCustomerId(customerId);
  }
  if (String(documentNumber || "").trim()) {
    payload.document_number = String(documentNumber || "").trim();
  }
  return api.post(`/db/billing/invoices/${Number(invoiceId || 0)}/email`, payload);
};

export const resolveInvoiceIdFromDocumentNumber = async ({ customerId, documentNumber }) => {
  const cid = Number(customerId || 0);
  const doc = String(documentNumber || "").trim();
  if (cid < 1 || !doc) {
    throw new Error("Invalid customer or invoice document number.");
  }
  const response = await api.post("/db/billing/invoices/resolve", {
    customer_id: cid,
    document_number: doc,
  });
  const invoiceId = Number(response?.invoice_id || 0);
  if (invoiceId < 1) {
    throw new Error("Invoice ID could not be resolved.");
  }
  return invoiceId;
};

const fetchBackendQuotePdfBlob = async (quoteId, mode) => {
  const id = Number(quoteId || 0);
  if (id < 1) {
    throw new Error("Invalid quote id.");
  }
  const endpoint = String(mode || "").trim() === "download" ? "download" : "view";
  const response = await fetch(`${API_CONFIG.baseUrl}/db/billing/quotes/${id}/${endpoint}`, {
    method: "GET",
    headers: {
      "X-API-Key": API_CONFIG.apiKey,
      Accept: "application/pdf",
    },
  });
  if (!response.ok) {
    let message = `Quotation ${endpoint} failed (HTTP ${response.status}).`;
    try {
      const err = await response.json();
      if (err?.error) {
        message = String(err.error);
      }
    } catch {
      // keep generic message if error body is not JSON
    }
    throw new Error(message);
  }
  return response.blob();
};

export const getQuotePdfPreviewBlob = async (quoteId) =>
  fetchBackendQuotePdfBlob(quoteId, "view");

export const downloadQuotePdf = async (quoteId, filenameOverride = "") => {
  const blob = await fetchBackendQuotePdfBlob(quoteId, "download");
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  const cleanFilename = String(filenameOverride || "").trim();
  link.download = cleanFilename || `quote_${Number(quoteId || 0)}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(objectUrl);
};

export const sendQuotePdfByEmail = async ({ quoteId, toEmail }) => {
  const payload = {};
  const email = String(toEmail || "").trim();
  if (email) {
    payload.to_email = email;
  }
  return api.post(`/db/billing/quotes/${Number(quoteId || 0)}/email`, payload);
};

export const sendQuotePdfByEmailWithContext = async ({
  quoteId,
  toEmail,
  customerId,
  documentNumber,
}) => {
  const payload = {};
  const email = String(toEmail || "").trim();
  if (email) {
    payload.to_email = email;
  }
  if (isValidCustomerId(customerId)) {
    payload.customer_id = parseCustomerId(customerId);
  }
  if (String(documentNumber || "").trim()) {
    payload.document_number = String(documentNumber || "").trim();
  }
  return api.post(`/db/billing/quotes/${Number(quoteId || 0)}/email`, payload);
};

export const resolveQuoteIdFromDocumentNumber = async ({ customerId, documentNumber }) => {
  const cid = Number(customerId || 0);
  const doc = String(documentNumber || "").trim();
  if (cid < 1 || !doc) {
    throw new Error("Invalid customer or quotation document number.");
  }
  const response = await api.post("/db/billing/quotes/resolve", {
    customer_id: cid,
    document_number: doc,
  });
  const quoteId = Number(response?.quote_id || 0);
  if (quoteId < 1) {
    throw new Error("Quotation ID could not be resolved.");
  }
  return quoteId;
};

const billingEnvelopeRequest = async (resource, request, parameters = {}) => {
  if (!String(API_CONFIG.billingApi.baseUrl || "").trim()) {
    throw new Error("Billing API URL is not configured (FRONTEND_BILLING_API_BASE_URL).");
  }
  if (!String(API_CONFIG.billingApi.apiKey || "").trim()) {
    throw new Error(
      API_CONFIG.billingApi.useProxy
        ? "Admin API key is not configured (FRONTEND_API_KEY)."
        : "Billing API key is not configured (FRONTEND_BILLING_API_KEY)."
    );
  }

  const requestData = API_CONFIG.billingApi.useProxy
    ? { resource, request, parameters }
    : {
        "api-key": API_CONFIG.billingApi.apiKey,
        resource,
        request,
        parameters,
      };

  const { ok, data, response, networkError } = await billingFetch(requestData);
  if (networkError) {
    throw new Error(balanceUnreachableMessage(networkError));
  }
  if (!ok) {
    const status = response?.status ?? "error";
    const detail =
      typeof data?.reply === "string"
        ? data.reply
        : typeof data?.message === "string"
          ? data.message
          : "Billing API request failed.";
    throw new Error(`Billing service error (HTTP ${status}): ${detail}`);
  }
  if (Number(data?.status) !== 200) {
    const reply = data?.reply;
    throw new Error(
      typeof reply === "string" ? reply : "Billing service returned an unexpected error."
    );
  }
  return data?.reply;
};

export const fetchBillingQuote = async (quoteId) => {
  const id = Number(quoteId || 0);
  if (id < 1) {
    throw new Error("Invalid quote id.");
  }
  const quote = await api.get(`/db/billing/quotes/${id}`);
  return quote && typeof quote === "object" ? quote : null;
};

export const createBillingQuote = async (parameters) =>
  billingEnvelopeRequest("quotes", "put", parameters || {});

export const updateBillingQuote = async (parameters) =>
  billingEnvelopeRequest("quotes", "post", parameters || {});

export const startBulkInvoiceEmailBatch = async ({
  startDate,
  endDate,
  perMessageDelaySeconds,
  toEmailOverride,
}) => {
  const payload = {
    start_date: String(startDate || "").trim(),
    end_date: String(endDate || "").trim(),
  };
  if (perMessageDelaySeconds !== undefined && perMessageDelaySeconds !== null && perMessageDelaySeconds !== "") {
    payload.per_message_delay_seconds = Number(perMessageDelaySeconds);
  }
  if (String(toEmailOverride || "").trim()) {
    payload.to_email_override = String(toEmailOverride).trim();
  }
  return api.post("/db/billing/invoices/batch/email", payload);
};

export const getBulkInvoiceEmailBatchStatus = async (jobId) =>
  api.get(`/db/billing/invoices/batch/${String(jobId || "").trim()}`);

export const cancelBulkInvoiceEmailBatch = async (jobId) =>
  api.post(`/db/billing/invoices/batch/${String(jobId || "").trim()}/cancel`, {});

export const runManualRecurringInvoices = async ({ startDate, endDate, dryRun = false }) =>
  api.post("/db/billing/recurring/manual-run", {
    start_date: String(startDate || "").trim(),
    end_date: String(endDate || "").trim(),
    dry_run: Boolean(dryRun),
  });

export const getManualRecurringRunStatus = async (jobId) =>
  api.get(`/db/billing/recurring/manual-run/${String(jobId || "").trim()}`);
